import type { Problem, ProblemCategory } from "@/types/problem"
import { getAllProblemsContent, getProblemContentBySlug } from "@/lib/problem-content"

const categoryLabels: Record<ProblemCategory, string> = {
  brine: "Розсіл і маринад",
  lids: "Кришки та герметичність",
  texture: "Текстура овочів",
  sterilization: "Стерилізація",
  fermentation: "Ферментація",
  storage: "Зберігання",
  marinade: "Маринад",
  safety: "Безпека",
  other: "Інше",
}

const severityLabels: Record<Problem["severity"], string> = {
  low: "Низька",
  medium: "Середня",
  high: "Висока",
}

function parseProblemDate(value: string | undefined) {
  if (!value) {
    return 0
  }

  const parsed = Date.parse(value)
  return Number.isNaN(parsed) ? 0 : parsed
}

function getSharedCount(left: string[] | undefined, right: string[] | undefined) {
  if (!left?.length || !right?.length) {
    return 0
  }

  const rightSet = new Set(right)
  return left.filter((item) => rightSet.has(item)).length
}

function scoreRelatedProblem(currentProblem: Problem, candidate: Problem) {
  let score = 0

  if (candidate.category === currentProblem.category) {
    score += 5
  }

  if (candidate.severity === currentProblem.severity) {
    score += 2
  }

  score += getSharedCount(candidate.tags, currentProblem.tags) * 3

  if (candidate.featured) {
    score += 0.5
  }

  return score
}

export async function getAllProblems() {
  return getAllProblemsContent()
}

export async function getFeaturedProblems(limit = 6) {
  const problems = await getAllProblems()
  return problems.filter((item) => item.featured).slice(0, limit)
}

export async function getAllProblemSlugs() {
  const problems = await getAllProblems()
  return problems.map((problem) => problem.slug)
}

export async function getProblemOrNull(slug: string) {
  return getProblemContentBySlug(slug)
}

export async function getRelatedProblems(currentProblem: Problem, limit = 3) {
  const allProblems = await getAllProblems()

  return allProblems
    .filter((problem) => problem.slug !== currentProblem.slug)
    .map((problem) => ({
      problem,
      score: scoreRelatedProblem(currentProblem, problem),
      updatedAtScore: parseProblemDate(problem.updatedAt ?? problem.publishedAt),
    }))
    .sort((a, b) => {
      if (b.score !== a.score) {
        return b.score - a.score
      }

      if (b.updatedAtScore !== a.updatedAtScore) {
        return b.updatedAtScore - a.updatedAtScore
      }

      return a.problem.title.localeCompare(b.problem.title, "uk")
    })
    .slice(0, limit)
    .map((item) => item.problem)
}

export async function getAllProblemCategories() {
  const problems = await getAllProblems()
  const categories = Array.from(new Set(problems.map((problem) => problem.category)))

  return categories
    .filter((category): category is ProblemCategory => isProblemCategory(category))
    .sort((left, right) =>
      getProblemCategoryLabel(left).localeCompare(getProblemCategoryLabel(right), "uk"),
    )
}

export async function getProblemsByCategory(category: ProblemCategory) {
  const problems = await getAllProblems()

  return problems
    .filter((problem) => problem.category === category)
    .sort((a, b) => {
      if (Boolean(b.featured) !== Boolean(a.featured)) {
        return Number(Boolean(b.featured)) - Number(Boolean(a.featured))
      }

      const leftUpdated = parseProblemDate(a.updatedAt ?? a.publishedAt)
      const rightUpdated = parseDateSafe(b.updatedAt ?? b.publishedAt)

      if (rightUpdated !== leftUpdated) {
        return rightUpdated - leftUpdated
      }

      return a.title.localeCompare(b.title, "uk")
    })
}

function parseDateSafe(value: string | undefined) {
  return parseProblemDate(value)
}

export async function getProblemCategoryStats() {
  const problems = await getAllProblems()
  const stats = new Map<ProblemCategory, number>()

  for (const problem of problems) {
    if (!isProblemCategory(problem.category)) {
      continue
    }

    stats.set(problem.category, (stats.get(problem.category) ?? 0) + 1)
  }

  return Array.from(stats.entries())
    .map(([category, count]) => ({
      category,
      label: getProblemCategoryLabel(category),
      count,
      href: `/problems/category/${category}`,
    }))
    .sort((a, b) => a.label.localeCompare(b.label, "uk"))
}

export function isProblemCategory(value: string): value is ProblemCategory {
  return value in categoryLabels
}

export function getProblemBreadcrumbs(problem: Problem) {
  return [
    { title: "Головна", href: "/" },
    { title: "Проблеми та рішення", href: "/problems" },
    { title: problem.title, href: `/problems/${problem.slug}` },
  ]
}

export function getProblemCategoryLabel(category: ProblemCategory) {
  return categoryLabels[category]
}

export function getProblemSeverityLabel(severity: Problem["severity"]) {
  return severityLabels[severity]
}